import React, {useCallback, useEffect, useMemo} from 'react';
import {Form} from 'antd';
import ProForm from '@ant-design/pro-form';
import debounce from 'lodash/debounce';
import set from 'lodash/set';
import isEqual from 'lodash/isEqual';
import get from 'lodash/get';
import UserDetails from '@/components/UserDrawer/Edit/UserDetails';
import UserWorkingHours from '@/components/UserDrawer/Edit/UserWorkingHours';
import {updateUserRequest} from '@/services/api/user';
import {processAPIResponseNow} from '@/utils/request';
import {INTL_ENTITY} from '@/utils/intl';
import {AssigneeModel} from '@/typings/models/Assignee';
import {useDispatch} from 'umi';
import {useHasAccess} from '@/utils/hooks';
import PERMISSIONS from '@/constants/permissions';

const AgentInfoEdit = ({agent, type, onDirty}: {agent: AssigneeModel, type?: string, onDirty?: () => void}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const hasFullEdit = useHasAccess([PERMISSIONS.Users.EDIT, PERMISSIONS.BUSINESS_PARTNER]);
  const hasAvailabilityEdit = useHasAccess([PERMISSIONS.Users.AVAILABILITY_EDIT, PERMISSIONS.BUSINESS_PARTNER]);
  const hasPriorityEdit = useHasAccess([PERMISSIONS.Users.PRIORITY_EDIT, PERMISSIONS.BUSINESS_PARTNER]);
  useEffect(() => {
    form.setFieldsValue({
      ...agent,
      roles: agent?.roles?.map((role) => role.id) || [],
      services: agent?.services?.map((service) => ({
        value: service.id,
        label: service,
        obj: service
      })) || [],
      branches: agent?.branches?.map((branch) => branch.id) || [],
    });
  }, [agent]);

  const debouncedUpdateUser = useMemo(
    () =>
      debounce((values) => {
        const reallyChangedValues = Object.keys(values).reduce((acc, key) => {
          if (!isEqual(values[key], get(agent, key))) {
            acc[key] = values[key];
          }
          return acc;
        }, {});

        if (Object.keys(reallyChangedValues).length === 0) return;
        onDirty?.();

        updateUserRequest({
          params: { userId: agent.id },
          payload: reallyChangedValues,
        }).then((response) => {
          processAPIResponseNow(response, {
            showSuccessMessage: true,
            successEntity: INTL_ENTITY.AGENT,
            onSuccess(data) {
              dispatch({
                type: 'user/fetchedAgent',
                payload: { id: agent.id, ...(data || reallyChangedValues) },
              });
            },
          }, 'data');
        });
      }, 400),
    [agent]
  );

  const handleBlur = useCallback((name, value) => {
    if (get(agent, name) === value) return;

    const payload = set({}, name, value);

    dispatch({
      type: 'user/fetchedAgent',
      payload: { id: agent.id, ...payload, },
    });
    updateUserRequest({
      params: { userId: agent.id },
      payload,
    }).then((response) => {
      processAPIResponseNow(response, {
        showSuccessMessage: true,
        successEntity: INTL_ENTITY.AGENT,
      }, 'data');
    });
  }, [agent]);

  const handleValuesChange = useCallback(
    (changedValues) => {
      const changedKeys = Object.keys(changedValues);

      // If it's a blur field, don't update immediately
      const isBlurField = changedKeys.some(key => {
        if (key === 'availability') {
          const subKeys = Object.keys(changedValues[key]);
          return subKeys.some(subKey => ['capacity', 'priority'].includes(subKey));
        }
        return ['firstname', 'lastname', 'email', 'phone', 'personal_id', 'migration_id', 'migration_source'].includes(key);
      });

      if (!isBlurField) {
        debouncedUpdateUser(changedValues);
      }
    },
    [debouncedUpdateUser]
  );

  useEffect(() => {
    return () => {
      debouncedUpdateUser.cancel();
    };
  }, [debouncedUpdateUser]);


  return (
        <ProForm form={form} submitter={false} onValuesChange={handleValuesChange}>
          {type === 'personal' ? <UserDetails userData={agent} useTransferableRoles={true} onFieldBlur={handleBlur} disabled={!hasFullEdit}/> : <UserWorkingHours user={agent} onFieldBlur={handleBlur} disableSettings={!hasFullEdit} disablePriority={!hasFullEdit && !hasPriorityEdit} hideAvailability={!hasFullEdit && !hasAvailabilityEdit}/>}
        </ProForm>
  );
};

export default AgentInfoEdit;
